import { useCallback, useId, type ChangeEvent } from 'react';
import { useLandmarkStore } from '../state/landmarks';
import { YEAR_MIN, YEAR_MAX, clampYear } from '../lib/time-overlay-mapping';

const TICKS = [1500, 1700, 1800, 1900, 2000, 2026];

/**
 * Year time-slider — top-center of the viewport, range YEAR_MIN..YEAR_MAX.
 *
 * Drives the historical overlays via the year-to-opacity mapping in
 * `lib/time-overlay-mapping.ts`. When the user drags, every change writes
 * to `setYear` which in turn updates the overlay state slice — Map.tsx
 * already reacts to that state, so the visual cross-fade is automatic.
 *
 * Initial state: `currentYear === null` means the slider hasn't been touched
 * (the layer panel's manual state is in effect). The first drag activates
 * the slider; clicking the "today" reset button releases it back to null.
 */
export function TimeSlider() {
  const currentYear = useLandmarkStore((state) => state.currentYear);
  const setYear = useLandmarkStore((state) => state.setYear);
  const inputId = useId();

  const displayYear = currentYear ?? YEAR_MAX;
  const isActive = currentYear !== null;

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = Number.parseInt(event.target.value, 10);
      setYear(clampYear(value));
    },
    [setYear],
  );

  const handleStep = useCallback(
    (delta: number) => {
      setYear(clampYear(displayYear + delta));
    },
    [setYear, displayYear],
  );

  const handleReset = useCallback(() => {
    setYear(null);
  }, [setYear]);

  return (
    <div
      data-testid="time-slider"
      role="group"
      aria-label="Year time-slider"
      style={{
        position: 'absolute',
        top: 'var(--space-3)',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 18,
        background: 'var(--surface-raised)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-card)',
        padding: 'var(--space-3) var(--space-4)',
        width: 'min(560px, calc(100vw - var(--space-12)))',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 'var(--space-2)' }}>
        <label
          htmlFor={inputId}
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: '1.1rem',
            fontWeight: 600,
            color: isActive ? 'var(--primary)' : 'var(--text-headline)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {displayYear}
        </label>
        <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
          <StepButton label="−10" onClick={() => handleStep(-10)} testId="time-slider-back-10" />
          <StepButton label="+10" onClick={() => handleStep(10)} testId="time-slider-forward-10" />
          {isActive ? (
            <button
              type="button"
              onClick={handleReset}
              data-testid="time-slider-reset"
              aria-label="Release the time slider — return to manual layer panel control"
              style={{
                marginLeft: 'var(--space-2)',
                padding: '2px 8px',
                fontSize: '0.7rem',
                color: 'var(--text-muted)',
                background: 'transparent',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
              }}
            >
              Today
            </button>
          ) : null}
        </div>
      </div>

      <input
        id={inputId}
        type="range"
        min={YEAR_MIN}
        max={YEAR_MAX}
        step={1}
        value={displayYear}
        onChange={handleChange}
        data-testid="time-slider-input"
        aria-label={`Year: ${displayYear}`}
        aria-valuemin={YEAR_MIN}
        aria-valuemax={YEAR_MAX}
        aria-valuenow={displayYear}
        list={`${inputId}-ticks`}
        style={{
          width: '100%',
          accentColor: 'var(--primary)',
        }}
      />
      <datalist id={`${inputId}-ticks`}>
        {TICKS.map((tick) => (
          <option key={tick} value={tick} label={String(tick)} />
        ))}
      </datalist>

      <div
        aria-hidden="true"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '0.65rem',
          color: 'var(--text-muted)',
          fontVariantNumeric: 'tabular-nums',
          marginTop: -4,
        }}
      >
        {TICKS.map((tick) => (
          <span
            key={tick}
            style={{
              position: 'relative',
              left: `${((tick - YEAR_MIN) / (YEAR_MAX - YEAR_MIN)) * 100 - 50}%`,
              width: 0,
              textAlign: 'center',
            }}
          >
            {tick}
          </span>
        ))}
      </div>
    </div>
  );
}

function StepButton({ label, onClick, testId }: { label: string; onClick: () => void; testId: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-label={`Year ${label}`}
      style={{
        padding: '2px 8px',
        fontSize: '0.75rem',
        color: 'var(--text-muted)',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-sm)',
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {label}
    </button>
  );
}
